import { CSSProperties, FC, ReactNode, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  Skeleton,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { SxProps } from "@mui/system";
import { Nullable } from "service";
import { useSearchParams } from "react-router-dom";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import GenericTableRows from "./GenericTableRows";
import DraggableTable from "./DraggableTable";

export interface LabelValueItem {
  label: string | ReactNode;
  value: string;
  onCellClick?: (value: string) => void;
  size?: string;
  minSize?: string;
  sortable?: boolean;
  sortableValue?: string;
  customCSS?: CSSProperties;
  minWidth?: string;
  index?: any;
}

type SortDirection = "asc" | "desc";

export interface SortOptions {
  sortBy: string; // Normally the value of the header
  direction: SortDirection;
}

export interface GenericTableRow {
  values: LabelValueItem[];
  item: any;
}

interface GenericTableHoverAction {
  node: ReactNode;
}

export type RenderGenericRowFunction<T = unknown> = (props: {
  header: LabelValueItem;
  data: T;
  headerValue: string;
  index: any;
  setActiveRow: React.Dispatch<React.SetStateAction<number | null>>;
}) => LabelValueItem;

export interface GenericTableProps {
  headers: LabelValueItem[];
  secondHeaders?: LabelValueItem[];
  renderRowLabel: Record<string, RenderGenericRowFunction<any>>;
  onRowClick?: (data: any, e: React.MouseEvent) => void;
  data: any[];
  onHoverActions?: GenericTableHoverAction[];
  sx?: SxProps<Theme>;
  sortOptions?: Nullable<SortOptions>;
  onSortChange?: (options: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  isHeaderSticky?: boolean;
  draggable?: boolean;
  shouldSetActiveRow?: boolean;
  shouldAddOnHover?: boolean;
  onAddClick?: (index: number) => void;
  defaultActiveRow?: number | null;
}

const GenericTable: FC<GenericTableProps> = (props) => {
  const [_, setSearchParams] = useSearchParams();
  const styles = useStyles();
  const [activeRow, setActiveRow] = useState<number | null>(
    props.defaultActiveRow === undefined ? null : props.defaultActiveRow,
  );
  const {
    headers,
    secondHeaders,
    renderRowLabel,
    onRowClick,
    data,
    sx,
    sortOptions,
    onSortChange,
    isLoading = false,
    isHeaderSticky = false,
    draggable = false,
    shouldSetActiveRow = true,
    shouldAddOnHover,
    onAddClick,
  } = props;

  const renderedRows = useMemo(() => {
    const result: GenericTableRow[] = [];
    let i = 0;
    for (const item of data) {
      const labels: LabelValueItem[] = [];
      i++;
      for (const header of headers) {
        const label = renderRowLabel[header.value]?.({
          header,
          data: item,
          headerValue: header.value,
          index: i,
          setActiveRow,
        });
        if (!label) {
          continue;
        }
        labels.push(label);
      }
      result.push({
        values: labels,
        item,
      });
    }
    return result;
  }, [headers, data, renderRowLabel]);

  const onSort = (value: string) => {
    if (!onSortChange) {
      return;
    }

    // Click on sort option and none is set currently
    if (!sortOptions || sortOptions.sortBy !== value) {
      onSortChange({
        sortBy: value,
        direction: "asc",
      });
      setSearchParams((params) => {
        params.set("sortBy", value);
        params.set("direction", "asc");
        return params;
      });
      return;
    }

    // Promote the already matching sort option to reversed ordering
    if (sortOptions.direction === "asc") {
      onSortChange({
        sortBy: value,
        direction: "desc",
      });
      setSearchParams((params) => {
        params.set("sortBy", value);
        params.set("direction", "desc");
        return params;
      });
      return;
    }

    // If already promoted unset the sorting entirelly
    onSortChange(null);
    setSearchParams((params) => {
      params.delete("sortBy");
      params.delete("direction");
      return params;
    });
  };

  const TableRowsLoader = ({
    rowsNum,
    cellNum,
  }: {
    rowsNum: number;
    cellNum: number;
  }) => {
    return (
      <>
        {[...Array(rowsNum)].map((_row, index) => (
          <TableRow sx={styles.tableRow} key={index}>
            <TableCell component="th" scope="row">
              <Skeleton animation="wave" variant="text" />
            </TableCell>
            {[...Array(cellNum - 1)].map((_cell, indexT) => (
              <TableCell key={indexT}>
                <Skeleton animation="wave" variant="text" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <TableContainer component={Paper} sx={sx} data-testid="generic-table">
      <Table sx={styles.table} stickyHeader={isHeaderSticky}>
        <TableHead sx={styles.tableHead}>
          <TableRow>
            {headers.map((h) => {
              const sortVal = h.sortableValue ? h.sortableValue : h.value;
              return (
                <TableCell
                  key={h.value}
                  sx={{
                    ...styles.tableHeadCell,
                    width: h.size,
                    ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                  }}
                  onClick={h.sortable ? () => onSort(sortVal) : undefined}
                >
                  <Box display="flex" alignItems="center" justifyContent="left">
                    <Typography variant="caption">{h.label}</Typography>
                    {h.sortable && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{
                          opacity: sortOptions?.sortBy === sortVal ? 1 : 0.5,
                        }}
                      >
                        <ArrowDropUp
                          sx={
                            sortOptions?.direction === "desc" &&
                            sortOptions?.sortBy === sortVal
                              ? { ...styles.icons, opacity: 0.5 }
                              : styles.icons
                          }
                        />
                        <ArrowDropDown
                          sx={
                            sortOptions?.direction === "asc" &&
                            sortOptions?.sortBy === sortVal
                              ? { ...styles.icons, opacity: 0.5 }
                              : styles.icons
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
          {secondHeaders && (
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
              }}
            >
              {secondHeaders.map((h) => {
                const sortVal = h.sortableValue ? h.sortableValue : h.value;
                return (
                  <TableCell
                    key={h.value}
                    sx={{
                      ...styles.tableHeadCell,
                      width: h.size,
                      ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                    }}
                    onClick={h.sortable ? () => onSort(sortVal) : undefined}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="left"
                    >
                      <Typography variant="caption">{h.label}</Typography>
                      {h.sortable && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{
                            opacity: sortOptions?.sortBy === sortVal ? 1 : 0.5,
                          }}
                        >
                          <ArrowDropUp
                            sx={
                              sortOptions?.direction === "desc" &&
                              sortOptions?.sortBy === sortVal
                                ? { ...styles.icons, opacity: 0.5 }
                                : styles.icons
                            }
                          />
                          <ArrowDropDown
                            sx={
                              sortOptions?.direction === "asc" &&
                              sortOptions?.sortBy === sortVal
                                ? { ...styles.icons, opacity: 0.5 }
                                : styles.icons
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {isLoading || !data ? (
            <TableRowsLoader rowsNum={10} cellNum={headers.length} />
          ) : draggable ? (
            <DraggableTable
              renderedRows={renderedRows}
              activeRow={activeRow}
              shouldAddOnHover={shouldAddOnHover}
              onAddClick={onAddClick}
            />
          ) : (
            <GenericTableRows
              renderedRows={renderedRows}
              headers={headers}
              onRowClick={onRowClick}
              activeRow={activeRow}
              setActiveRow={shouldSetActiveRow ? setActiveRow : undefined}
              draggable={false}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  tableHead: {
    height: "70px",
  },
  tableHeadCell: {
    fontWeight: 500,
    background: grey["500"],
  },
  tableColumnCell: {
    fontWeight: 400,
  },
  tableRow: {
    height: "70px",
    background: grey["800"],
    ".toShow": {
      display: "none",
    },
    "&:hover": {
      ".toHide": {
        display: "none",
      },
      ".toShow": {
        display: "flex",
      },
    },
    "&:visited": {
      outline: "1px solid #fff",
    },
  },
  activeRow: {
    outline: "1px solid #fff",
  },
  tableRowClickable: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  table: {
    width: "100%",
  },
  container: {
    padding: "10px 20px 30px",
  },
  loader: {
    margin: "0 auto",
  },
  icons: {
    margin: "-8px 0",
    color: grey["100"],
  },
}));

export default GenericTable;
